import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faSliders } from '@fortawesome/pro-light-svg-icons/faSliders';
import { TypedMemo } from '../../../common/types';
import { SMLGenreSelector } from '../../components/SMLGenreSelector/SMLGenreSelector';
import { SMLCountrySelector } from '../../components/SMLCountrySelector/SMLCountrySelector';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { hasBrowseFilter, type BrowseUrlPatch, type BrowseUrlState } from '../hooks/useBrowseUrlState';
import { browseMessages as messages } from '../browseMessages';
import { BrowseSortSelect } from './BrowseSortSelect';
import { BrowseServiceSelect } from './BrowseServiceSelect';
import { BrowseLockedFilter } from './BrowseLockedFilter';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseSearch.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseFiltersProps {
  state: BrowseUrlState;
  update: (patch: BrowseUrlPatch) => void;
  onPasteLink: () => void;
  onOpenFilterSheet: () => void;
}

export const CLEARED_FILTERS: BrowseUrlPatch = {
  genre: undefined,
  country: undefined,
  service: undefined,
  hideFollowed: undefined,
};

/**
 * The filter row (design a1, a2): sort, genre, country (Manager), service (Label), Hide followed,
 * Clear filters, Paste a playlist link. A phone gets one Filters chip that opens the sheet (c1, c2).
 */
const BrowseFiltersComponent: React.FC<BrowseFiltersProps> = ({ state, update, onPasteLink, onOpenFilterSheet }) => {
  const intl = useIntl();
  const capabilities = useMusic24Capabilities();
  const filterOn = hasBrowseFilter(state);

  return (
    <>
      <div className={clsx(styles.filters, styles.desktopFilters)}>
        <BrowseSortSelect
          value={state.sort}
          onChange={(sort) => update({ sort: sort === 'match' ? undefined : sort })}
        />
        <SMLGenreSelector />
        {capabilities.canFilterGeographically ? (
          <SMLCountrySelector />
        ) : (
          <BrowseLockedFilter
            label={intl.formatMessage(messages.countryLabel)}
            value={intl.formatMessage(messages.globalValue)}
            feature={SMLUpgradeFeature.geography}
          />
        )}
        {capabilities.canFilterByMusicService ? (
          <BrowseServiceSelect value={state.service} onChange={(service) => update({ service })} />
        ) : (
          <BrowseLockedFilter
            label={intl.formatMessage(messages.serviceLabel)}
            value={intl.formatMessage(messages.serviceAll)}
            feature={SMLUpgradeFeature.musicServiceFilter}
          />
        )}
        <span className={styles.separator} aria-hidden="true" />
        <button
          type="button"
          className={styles.toggle}
          role="switch"
          aria-checked={state.hideFollowed}
          onClick={() => update({ hideFollowed: state.hideFollowed ? undefined : '1' })}
          data-testid="browse-hide-followed"
        >
          <span className={clsx(styles.switchTrack, state.hideFollowed && styles.switchOn)} aria-hidden="true" />
          {intl.formatMessage(messages.hideFollowed)}
        </button>
        {filterOn && (
          <button type="button" className={styles.textButton} onClick={() => update(CLEARED_FILTERS)}>
            {intl.formatMessage(messages.clearFilters)}
          </button>
        )}
        <button type="button" className={clsx(styles.textButton, styles.pushRight)} onClick={onPasteLink}>
          <FontAwesomeIcon icon={faLink} />
          {intl.formatMessage(messages.pasteLink)}
        </button>
      </div>
      <div className={styles.phoneFilters}>
        <button
          type="button"
          className={clsx(styles.dropdown, filterOn && styles.dropdownSet)}
          onClick={onOpenFilterSheet}
          data-testid="browse-filters-chip"
        >
          <FontAwesomeIcon icon={faSliders} />
          {intl.formatMessage(messages.filters)}
        </button>
        <button type="button" className={styles.dropdown} onClick={onPasteLink}>
          <FontAwesomeIcon icon={faLink} />
          {intl.formatMessage(messages.pasteLink)}
        </button>
      </div>
    </>
  );
};

export const BrowseFilters = TypedMemo(BrowseFiltersComponent);
